import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { rule } from '../lib/fieldRules.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { useFetch } from '../hooks/useFetch.js';
import { Seo } from '../components/Seo.jsx';
import { Stepper } from '../components/Stepper.jsx';
import { TermsGrid } from '../components/Terms.jsx';
import { TrackingLink } from '../components/TrackingLink.jsx';
import { SignInButtons } from '../components/SignInButtons.jsx';
import { GiftPicker, giftsPayload } from '../components/GiftPicker.jsx';
import {
  SocialConnect,
  useSocialAccounts,
  needsSocial,
  isSocialRequiredError,
} from '../components/SocialConnect.jsx';
import { Button, Textarea, Input, Field, Alert, Card, Spinner } from '../components/ui.jsx';

const STEPS = [
  { key: 'apply', label: 'Apply' },
  { key: 'done', label: 'Done' },
];

/**
 * Applying to a program, in two steps: the application itself, then what happens
 * next.
 *
 * The terms are restated in full on step one. A creator should be agreeing to
 * something visible at the moment they agree to it, rather than to something
 * they read on a different page some time earlier.
 *
 * A visitor with no account does not have to leave: the account fields sit in
 * the same form, and one click creates the account and sends the application.
 *
 * Applying needs a connected social account. A creator without one connects it
 * here; the platform sends them back to this page, with their message kept.
 */

/** The message survives the trip to the platform and back. Per tab; storage may be off. */
const draftKey = (slug) => `affily:apply-message:${slug}`;
function readDraft(slug) {
  try {
    return sessionStorage.getItem(draftKey(slug)) ?? '';
  } catch {
    return '';
  }
}
function writeDraft(slug, value) {
  try {
    if (value) sessionStorage.setItem(draftKey(slug), value);
    else sessionStorage.removeItem(draftKey(slug));
  } catch {
    // Nothing to do: the message just won't come back.
  }
}

export default function Apply() {
  const { slug } = useParams();
  const { user, loading: authLoading, isCreator, signup, addRole } = useAuth();
  const { data, loading, error: loadError } = useFetch(`/programs/${slug}`);
  const social = useSocialAccounts({ skip: !user || !isCreator });

  const [step, setStep] = useState('apply');
  const [result, setResult] = useState(null);

  const [account, setAccount] = useState({ name: '', email: '', password: '' });
  const [message, setMessageState] = useState(() => readDraft(slug));
  const setMessage = (value) => {
    setMessageState(value);
    writeDraft(slug, value);
  };
  // Set when the server refuses for want of an account (a brand-new one, say).
  const [refusedForSocial, setRefusedForSocial] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [signInHint, setSignInHint] = useState(false);
  const [busy, setBusy] = useState(false);
  // Gifts asked for along with the application: [{ giftKey, details }].
  const [gifts, setGifts] = useState([]);

  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [step]);

  if (loading || authLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">{loadError.message}</h1>
        <Button to="/programs" variant="outline" className="mt-6">
          Browse programs
        </Button>
      </div>
    );
  }

  const program = data.program;
  const brandName = program.brand?.companyName ?? 'the brand';
  const setAcct = (key) => (e) => setAccount((a) => ({ ...a, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    setSignInHint(false);

    // Account first. If this succeeds and the application then fails, the user
    // is already signed in, so a retry skips straight to the application.
    let stage = 'account';
    try {
      if (!user) {
        await signup({ ...account, role: 'creator' });
      } else if (!isCreator) {
        await addRole('creator');
      }
      stage = 'application';

      const res = await api.post('/applications', {
        programId: program.id,
        message,
        ...(gifts.length ? { gifts: giftsPayload(gifts, program.gifts) } : {}),
      });

      // Best effort: the "I came here for this program" note has done its job.
      api.patch('/onboarding/creator', { intentProgramSlug: null }).catch(() => {});
      writeDraft(slug, '');

      setResult(res);
      setStep('done');
    } catch (err) {
      if (isSocialRequiredError(err)) {
        setRefusedForSocial(true);
        social.reload();
        return;
      }
      setError(err.message);
      setFieldErrors(err.details ?? {});
      if (stage === 'account' && err.status === 409) setSignInHint(true);
    } finally {
      setBusy(false);
    }
  }

  // Sign-in sends them back here, not to the dashboard, so the application they
  // came to make is still in front of them.
  const loginHref = `/login?next=${encodeURIComponent(`/programs/${program.slug}/apply`)}`;
  const mustConnect = refusedForSocial || Boolean(user && isCreator && needsSocial(social.data));
  const trackingUrl = result?.partnership?.trackingLinks?.[0]?.url;

  return (
    <>
      <Seo title={`Apply — ${program.title}`} noindex />

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <nav className="text-sm text-ink-muted">
          <Link to={`/programs/${program.slug}`} className="hover:text-ink">
            ← {program.title}
          </Link>
        </nav>

        <Stepper steps={STEPS} current={step} complete={step === 'done' ? ['apply', 'done'] : []} className="mt-5" />

        {step === 'done' ? (
          <Done program={program} brandName={brandName} result={result} trackingUrl={trackingUrl} />
        ) : (
          <>
            <h1 className="mt-6 text-3xl font-semibold tracking-tight">Apply to this program</h1>
            {program.termsHidden ? (
              <p className="mt-2 text-ink-muted">
                This is a private program: {brandName} shares its terms with the creators it
                accepts. You’ll see them as soon as you’re accepted, and they’re frozen onto your
                partnership from then on.
              </p>
            ) : (
              <>
                <p className="mt-2 text-ink-muted">
                  These are the exact terms that get frozen onto your partnership if the brand accepts.
                  Later edits to the program won’t change them.
                </p>
                <TermsGrid program={program} className="mt-6" />
              </>
            )}

            {program.contentRequirements && (
              <section className="mt-6 rounded-lg border border-rule bg-paper-raised p-4">
                <h2 className="text-sm font-semibold">Content requirements</h2>
                <p className="mt-1.5 whitespace-pre-line text-sm text-ink-muted">
                  {program.contentRequirements}
                </p>
              </section>
            )}

            {program.status !== 'active' ? (
              <Alert tone="caution" className="mt-8">
                This program isn’t accepting applications right now. The terms stay public either
                way.
              </Alert>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4">
                <Alert>
                  {error}
                  {signInHint && (
                    <>
                      {' '}
                      <Link to={loginHref} className="font-medium underline">
                        Sign in instead
                      </Link>
                    </>
                  )}
                </Alert>

                {!user ? (
                  <Card className="space-y-4 p-4 sm:p-5">
                    <div>
                      <h2 className="text-sm font-semibold">Your account</h2>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        Free. Creating it and sending this application happen in one click.{' '}
                        <Link to={loginHref} className="text-accent hover:underline">
                          Already have one? Sign in
                        </Link>
                      </p>
                    </div>

                    <SignInButtons
                      role="creator"
                      next={`/programs/${slug}/apply`}
                      onSuccess={() => setError('')}
                      onError={setError}
                    />

                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Name" required error={fieldErrors.name} constraint={rule.max(120)}>
                        <Input
                          value={account.name}
                          onChange={setAcct('name')}
                          autoComplete="name"
                          required
                          maxLength={120}
                        />
                      </Field>
                      {/* "username", not "email": see Signup.jsx. */}
                      <Field
                        label="Email"
                        required
                        error={fieldErrors.email}
                        constraint={rule.email()}
                      >
                        <Input
                          type="email"
                          name="email"
                          value={account.email}
                          onChange={setAcct('email')}
                          autoComplete="username"
                          required
                        />
                      </Field>
                      <Field
                        label="Password"
                        required
                        error={fieldErrors.password}
                        constraint={rule.password()}
                        className="sm:col-span-2"
                      >
                        <Input
                          type="password"
                          name="password"
                          value={account.password}
                          onChange={setAcct('password')}
                          autoComplete="new-password"
                          minLength={10}
                          required
                        />
                      </Field>
                    </div>
                  </Card>
                ) : (
                  <p className="text-sm text-ink-muted">
                    Applying as <span className="font-medium text-ink">{user.name ?? user.email}</span>
                    {!isCreator && ' — we’ll add a creator profile to your account.'}
                  </p>
                )}

                <Field
                  label="Message to the brand"
                  constraint={rule.max(2000)}
                  hint="Optional. Affily has no inbox — this is the only message you can send, so make it count."
                >
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="What you make, who watches, and why this product fits them."
                  />
                </Field>

                {mustConnect && (
                  <Card className="space-y-3 p-4 sm:p-5">
                    <div>
                      <h2 className="text-sm font-semibold">Connect an account to apply</h2>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {brandName} sees the followers your platform reports, not numbers typed in.
                        One account is enough; you’ll come straight back here.
                      </p>
                    </div>
                    <SocialConnect social={social} next={`/programs/${program.slug}/apply`} />
                  </Card>
                )}

                <GiftPicker
                  autoApprove={Boolean(program.autoApprove)}
                  currency={program.currency}
                  gifts={program.gifts}
                  value={gifts}
                  onChange={setGifts}
                  errors={fieldErrors}
                />

                <div className="flex flex-wrap items-center gap-3 border-t border-rule pt-5">
                  <Button type="submit" size="lg" disabled={busy || mustConnect}>
                    {busy
                      ? 'Submitting…'
                      : program.autoApprove
                        ? user
                          ? 'Apply — starts immediately'
                          : 'Create account and apply — starts immediately'
                        : user
                          ? 'Send application'
                          : 'Create account and send application'}
                  </Button>
                  <Button to={`/programs/${program.slug}`} variant="ghost">
                    Back
                  </Button>
                </div>

                <p className="text-xs text-ink-faint">
                  Affily’s fee on commissions it tracks and pays out is on the{' '}
                  <Link to="/dashboard/pricing" className="text-accent hover:underline">
                    pricing page in your account
                  </Link>
                  , and on every commission line before it reaches you.
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Done({ program, brandName, result, trackingUrl }) {
  const approved = Boolean(result?.partnership);

  if (!approved) {
    return (
      <div className="mt-6">
        <h1 className="text-3xl font-semibold tracking-tight">Applied</h1>
        <p className="mt-2 max-w-xl text-ink-muted">
          Applied — we’ll email you when {brandName} decides. Nothing else is needed from you for
          now.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button to="/dashboard/creator/applications">See your applications</Button>
          <Button to="/programs" variant="outline">
            Browse more programs
          </Button>
        </div>
        <NextSteps />
      </div>
    );
  }

  // On an auto-approved program the gift requests are settled in the same call,
  // so say what happened to them rather than leaving "the brand approves each
  // one" to imply a wait that isn't there.
  const giftRequests = result?.giftRequests ?? [];
  const approvedGifts = giftRequests.filter((r) => r.status === 'approved').length;
  const declinedGifts = giftRequests.filter((r) => r.status === 'declined').length;

  return (
    <div className="mt-6">
      <h1 className="text-3xl font-semibold tracking-tight">You’re in</h1>
      <p className="mt-2 max-w-xl text-ink-muted">
        {program.title} started straight away. Here is your tracking link — clicks on it are
        counted against you.
      </p>
      {approvedGifts > 0 && (
        <p className="mt-2 max-w-xl text-ink-muted">
          {approvedGifts === 1
            ? 'Your gift request is approved — the brand will ship it.'
            : `Your ${approvedGifts} gift requests are approved — the brand will ship them.`}
        </p>
      )}
      {declinedGifts > 0 && (
        <Alert tone="caution" className="mt-3 max-w-xl">
          {declinedGifts === 1
            ? 'One gift you asked for was out of stock, so it was declined.'
            : `${declinedGifts} gifts you asked for were out of stock, so they were declined.`}
        </Alert>
      )}

      <Card className="mt-6 p-4">
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
          Your tracking link
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {trackingUrl ? (
            <TrackingLink url={trackingUrl} />
          ) : (
            <p className="text-sm text-ink-muted">
              Your link is on the{' '}
              <Link to="/dashboard/creator/partnerships" className="text-accent hover:underline">
                partnerships page
              </Link>
              .
            </p>
          )}
        </div>
        {result.partnership.promoCode && (
          <>
            <p className="mt-4 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              Your creator code
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <TrackingLink url={result.partnership.promoCode} noun="code" />
              {result.partnership.codeDiscount && (
                <span className="text-xs text-ink-faint">
                  {result.partnership.codeDiscount} for shoppers
                </span>
              )}
            </div>
            <p className="mt-1.5 text-xs text-ink-muted">
              For podcasts and video: sales using this code count for you, no click needed.
            </p>
          </>
        )}
      </Card>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button to="/dashboard/creator/partnerships">Go to partnerships</Button>
        <Button to="/programs" variant="outline">
          Browse more programs
        </Button>
      </div>
      <NextSteps gifts={program.gifts?.length > 0} />
    </div>
  );
}

function NextSteps({ gifts = false }) {
  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold">What next</h2>
      <ul className="mt-2 list-inside list-disc space-y-1.5 text-sm text-ink-muted">
        <li>
          <Link to="/dashboard/creator/profile" className="text-accent hover:underline">
            Publish your profile
          </Link>{' '}
          when you’re ready. It stays private until you do.
        </li>
        {gifts && (
          <li>
            Request gifts from the program on your{' '}
            <Link to="/dashboard/creator/partnerships" className="text-accent hover:underline">
              partnerships page
            </Link>
            .
          </li>
        )}
        <li>
          Watch{' '}
          <Link to="/dashboard/creator/earnings" className="text-accent hover:underline">
            your earnings
          </Link>
          . Clicks and conversions show up as they’re tracked, including the bad days.
        </li>
      </ul>
    </section>
  );
}
